import React, { useEffect, useMemo, useState } from 'react';

import { useGetCardsCount, useGetCards } from '@vlabs/api/artemy/client';
import type { GetCardsParams } from '@vlabs/api/artemy/model';
import { ListIcon, ViewGridIcon } from '@vlabs/icons';
import { CardListFastFiltersPoint3 } from '@vlabs/monokit/entities/cards/filters';
import { useCardsFilters } from '@vlabs/monokit/entities/cards/filters/useCardsFilters';
import { useWildcardCardListFiltersValue } from '@vlabs/monokit/entities/cards/filters/useWildcardFiltersValue';
import { SortCardsDropdown } from '@vlabs/monokit/entities/cards/sort-cards';
import { PageSubheader } from '@vlabs/monokit/shared/ui/page';
import {
  ButtonSwitch, ButtonSwitchGroup, PaginationPanel,
} from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import st from './CardListViewer.module.sass';
import { CardListGridView } from './grid-view';
import { CardListTableView } from './table-view';

type TViewerType = 'table' | 'grid';

class Storage<T extends string> {
  storage = sessionStorage;

  storageKey = 'card-list-page';

  path: string;

  constructor(path: string) {
    this.path = path;
  }

  get pathKey(): string {
    return [this.storageKey, this.path].join('.');
  }

  save(value: T) {
    this.storage.setItem(this.pathKey, value);
  }

  public get value(): T | null {
    return this.storage.getItem(this.pathKey) as T | null;
  }
}

const viewerTypeStorage = new Storage<TViewerType>('viewer_type');

export function CardListViewer(): React.JSX.Element {
  const { t } = useTranslation();
  const [viewerType, setViewerType] = useState<TViewerType>(() => viewerTypeStorage.value ?? 'table');
  const [selectedIds, setSelectedIds] = React.useState<Record<string, boolean>>({});
  const { filters, updateFilters } = useCardsFilters();
  const wildCardFilters = useWildcardCardListFiltersValue();
  const getCardCount = useGetCardsCount(wildCardFilters);
  const getCards = useGetCards(wildCardFilters as GetCardsParams);

  useEffect(() => setSelectedIds({}), [getCards.data]);

  return (
    <React.Fragment>
      <PageSubheader
        className={st.Subheader}
        title={(
          <div className={st.Title}>
            {t('card-list-page:cards found', { count: getCardCount?.data?.cards_count ?? 0 })}
          </div>
        )}
      >
        <div className={st.SubheaderContent}>
          <div className={st.SubheaderFastFilters}>
            <CardListFastFiltersPoint3 />
          </div>
          <div className={st.SubheaderActions}>
            <SortCardsDropdown />
            <ButtonSwitchGroup
              onChange={(newViewerType) => {
                setViewerType(newViewerType);
                viewerTypeStorage.save(newViewerType);
              }}
              value={viewerType}
            >
              <ButtonSwitch
                icon={<ListIcon />}
                value="table"
              />
              <ButtonSwitch
                icon={<ViewGridIcon />}
                value="grid"
              />
            </ButtonSwitchGroup>
          </div>
        </div>
      </PageSubheader>
      <div className={st.Root}>
        {viewerType === 'table' && (
          <CardListTableView
            data={getCards?.data?.cards ?? []}
            onSelect={setSelectedIds}
            selectedIds={selectedIds}
          />
        )}
        {viewerType === 'grid' && (
          <CardListGridView
            data={getCards?.data?.cards}
            onSelect={setSelectedIds}
            selectedCardList={selectedIds}
          />
        )}
        <div className={st.Pagination}>
          <PaginationPanel
            canNextPage={(getCards?.data?.cards?.length ?? 0) >= (filters.page_size ?? 0)}
            canPreviousPage={(filters?.page ?? 0) > 0}
            gotoPage={(page) => updateFilters({ page: page + 1 })}
            nextPage={() => updateFilters({ page: (filters?.page ?? 0) + 1 })}
            pageCount={Math.ceil(((getCardCount?.data?.cards_count ?? 0) / (filters?.page_size ?? 25)))}
            pageIndex={(filters?.page ?? 1) - 1}
            pageSize={filters.page_size}
            previousPage={() => updateFilters({ page: (filters?.page ?? 0) - 1 })}
            setPageSize={(page_size: number) => updateFilters({ page_size })}
          />
        </div>
      </div>
    </React.Fragment>
  );
}
